import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend, 
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, 
  ScatterChart, Scatter, ZAxis, RadarChart, PolarGrid, 
  PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { MapContainer, TileLayer, Circle, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Sample competitor data
const competitorData = [
  { id: 1, name: 'Competitor A', distance: 0.8, priceGasohol95: 33.25, priceDiesel: 30.20, ammenities: ['Store', 'Coffee', 'ATM'], trafficShare: 22, lat: 13.7317, lng: 100.5857 },
  { id: 2, name: 'Competitor B', distance: 1.2, priceGasohol95: 33.15, priceDiesel: 30.30, ammenities: ['Store', 'Restroom'], trafficShare: 18, lat: 13.7327, lng: 100.5867 },
  { id: 3, name: 'Competitor C', distance: 2.5, priceGasohol95: 33.35, priceDiesel: 30.10, ammenities: ['Store', 'Coffee', 'Food', 'Car Wash'], trafficShare: 15, lat: 13.7297, lng: 100.5837 },
  { id: 4, name: 'Competitor D', distance: 3.1, priceGasohol95: 33.20, priceDiesel: 30.25, ammenities: ['Store'], trafficShare: 12, lat: 13.7277, lng: 100.5827 },
  { id: 5, name: 'Competitor E', distance: 4.2, priceGasohol95: 33.10, priceDiesel: 30.15, ammenities: ['Store', 'Car Wash'], trafficShare: 8, lat: 13.7347, lng: 100.5887 },
];

// Historical price trends
const priceHistoryData = [
  { month: 'Jan', ourPrice: 32.75, avgCompetitorPrice: 33.10, marketAverage: 33.00 },
  { month: 'Feb', ourPrice: 33.15, avgCompetitorPrice: 33.25, marketAverage: 33.20 },
  { month: 'Mar', ourPrice: 33.40, avgCompetitorPrice: 33.50, marketAverage: 33.45 },
  { month: 'Apr', ourPrice: 33.80, avgCompetitorPrice: 33.90, marketAverage: 33.85 },
  { month: 'May', ourPrice: 33.50, avgCompetitorPrice: 33.60, marketAverage: 33.55 },
  { month: 'Jun', ourPrice: 33.20, avgCompetitorPrice: 33.35, marketAverage: 33.30 },
  { month: 'Jul', ourPrice: 33.30, avgCompetitorPrice: 33.40, marketAverage: 33.35 },
  { month: 'Aug', ourPrice: 33.25, avgCompetitorPrice: 33.35, marketAverage: 33.30 },
];

// Competitor strengths radar data
const strengthsData = [
  { subject: 'Price', ours: 8, competitorA: 9, competitorB: 7 },
  { subject: 'Service', ours: 9, competitorA: 7, competitorB: 8 },
  { subject: 'Location', ours: 9, competitorA: 8, competitorB: 6 },
  { subject: 'Ammenities', ours: 8, competitorA: 9, competitorB: 5 },
  { subject: 'Brand', ours: 9, competitorA: 7, competitorB: 8 },
  { subject: 'Loyalty Program', ours: 8, competitorA: 6, competitorB: 7 },
];

// Customer overlap data
const overlapData = [
  { name: 'Exclusive to us', value: 55 },
  { name: 'Shared with competitors', value: 35 },
  { name: 'Exclusive to competitors', value: 10 },
];

// Price sensitivity analysis
const sensitivityData = [
  { priceDiff: -0.50, volumeChange: +8.2 },
  { priceDiff: -0.40, volumeChange: +6.5 },
  { priceDiff: -0.30, volumeChange: +4.7 },
  { priceDiff: -0.20, volumeChange: +3.1 },
  { priceDiff: -0.10, volumeChange: +1.5 },
  { priceDiff: 0, volumeChange: 0 },
  { priceDiff: +0.10, volumeChange: -1.8 },
  { priceDiff: +0.20, volumeChange: -3.4 },
  { priceDiff: +0.30, volumeChange: -5.2 },
  { priceDiff: +0.40, volumeChange: -7.1 },
  { priceDiff: +0.50, volumeChange: -9.3 },
];

// Feature comparison data
const featureComparisonData = [
  { feature: '24/7 Service', ours: true, competitorA: true, competitorB: false, competitorC: true },
  { feature: 'Premium Fuel', ours: true, competitorA: true, competitorB: true, competitorC: false },
  { feature: 'Car Wash', ours: true, competitorA: false, competitorB: true, competitorC: true },
  { feature: 'EV Charging', ours: true, competitorA: false, competitorB: false, competitorC: false },
  { feature: 'Loyalty Program', ours: true, competitorA: true, competitorB: true, competitorC: false },
  { feature: 'Mobile App', ours: true, competitorA: true, competitorB: false, competitorC: false },
  { feature: 'Food Service', ours: true, competitorA: true, competitorB: false, competitorC: true },
  { feature: 'ATM', ours: true, competitorA: true, competitorB: true, competitorC: true },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#ff00ff'];

const CompetitorAnalysis = ({ station }) => {
  // Fixed values as requested
  const [analysisType, setAnalysisType] = useState('pricing');
  const comparisonRadius = 3; // Fixed at 3km
  const selectedCompetitors = ['Competitor A', 'Competitor B']; // Fixed selected competitors
  const timeFrame = '1year'; // Fixed at 'Last Year'
  
  // Filter competitors based on fixed radius
  const filteredCompetitors = competitorData.filter(competitor => 
    competitor.distance <= comparisonRadius
  );
  
  // Generate AI insights based on current data and selection
  const generateInsights = () => {
    // Base insights
    let insights = [
      `There are ${filteredCompetitors.length} competitors within ${comparisonRadius}km of your station.`,
    ];
    
    // Add pricing insights
    if (analysisType === 'pricing') {
      const ourLatestPrice = priceHistoryData[priceHistoryData.length - 1].ourPrice;
      const avgCompPrice = priceHistoryData[priceHistoryData.length - 1].avgCompetitorPrice;
      
      if (ourLatestPrice < avgCompPrice) {
        insights.push(`Your current Gasohol 95 price (${ourLatestPrice}฿) is ${(avgCompPrice - ourLatestPrice).toFixed(2)}฿ lower than the average competitor price.`);
        insights.push(`This price advantage could be leveraged in marketing communications.`);
      } else if (ourLatestPrice > avgCompPrice) {
        insights.push(`Your current Gasohol 95 price (${ourLatestPrice}฿) is ${(ourLatestPrice - avgCompPrice).toFixed(2)}฿ higher than the average competitor price.`);
        insights.push(`Consider reviewing pricing strategy to remain competitive or emphasize your premium service offerings.`);
      } else {
        insights.push(`Your current pricing is at the market average. Consider differentiation through service quality or promotions.`);
      }
    }
    
    // Add feature differentiation insights
    if (analysisType === 'features') {
      const uniqueFeatures = featureComparisonData.filter(f => 
        f.ours && (!f.competitorA && !f.competitorB && !f.competitorC)
      );
      
      if (uniqueFeatures.length > 0) {
        insights.push(`You have ${uniqueFeatures.length} unique features that competitors lack: ${uniqueFeatures.map(f => f.feature).join(', ')}.`);
        insights.push(`These features should be highlighted in your marketing to differentiate your station.`);
      } else {
        insights.push(`You don't have unique features compared to competitors. Consider introducing new services to differentiate.`);
      }
    }
    
    // Add competitive pressure insights
    if (filteredCompetitors.length > 2) {
      insights.push(`The high density of competitors suggests a highly contested market. Focus on loyalty programs and exceptional service.`);
    } else if (filteredCompetitors.length <= 1) {
      insights.push(`Limited direct competition in your immediate vicinity suggests opportunity for premium pricing or market expansion.`);
    }
    
    return insights;
  };
  
  const insights = generateInsights();

  // Calculate our station for map
  const ourStation = {
    id: 'our',
    name: station.name,
    lat: station.lat,
    lng: station.lng
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Competitor Analysis for {station.name}
        </h3>
        
        <div className="flex space-x-2">
          <button
            onClick={() => setAnalysisType('pricing')}
            className={`px-3 py-1 text-sm rounded-md ${
              analysisType === 'pricing' 
                ? 'bg-purple-100 text-purple-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Pricing Analysis
          </button>
          
          <button
            onClick={() => setAnalysisType('landscape')}
            className={`px-3 py-1 text-sm rounded-md ${
              analysisType === 'landscape' 
                ? 'bg-purple-100 text-purple-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Competitive Landscape
          </button>
          
          <button
            onClick={() => setAnalysisType('features')}
            className={`px-3 py-1 text-sm rounded-md ${
              analysisType === 'features' 
                ? 'bg-purple-100 text-purple-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Feature Comparison
          </button>
          
          <button
            onClick={() => setAnalysisType('insights')}
            className={`px-3 py-1 text-sm rounded-md ${
              analysisType === 'insights' 
                ? 'bg-purple-100 text-purple-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            AI Insights
          </button>
        </div>
      </div>
      
      {/* Interactive Controls - Now visible but disabled */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Comparison Radius Slider - Fixed at 3km */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Comparison Radius</label>
              <span className="text-sm text-purple-700 font-medium">{comparisonRadius} km</span>
            </div>
            <input
              type="range"
              min={1}
              max={5}
              step={0.5}
              value={comparisonRadius}
              onChange={(e) => {/* No change allowed */}}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-not-allowed accent-purple-600"
              disabled={true}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>1 km</span>
              <span>3 km</span>
              <span>5 km</span>
            </div>
          </div>
          
          {/* Competitor Selection - Fixed selection */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">Compare With</label>
            <div className="space-y-2 max-h-20 overflow-y-auto">
              {competitorData.slice(0, 3).map(competitor => (
                <div key={competitor.id} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`comp-${competitor.id}`}
                    checked={selectedCompetitors.includes(competitor.name)}
                    onChange={(e) => {/* No change allowed */}}
                    className="h-4 w-4 text-purple-600 border-gray-300 rounded cursor-not-allowed"
                    disabled={true}
                  />
                  <label htmlFor={`comp-${competitor.id}`} className="ml-2 text-sm text-gray-700">
                    {competitor.name} ({competitor.distance}km)
                  </label>
                </div>
              ))}
            </div>
          </div>
          
          {/* Time Frame Selection - Fixed at "Last Year" */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">Time Frame</label>
            <select 
              className="w-full p-2 bg-white border border-gray-300 rounded-md text-sm cursor-not-allowed"
              value={timeFrame}
              onChange={(e) => {/* No change allowed */}}
              disabled={true}
            >
              <option value="1month">Last Month</option>
              <option value="3months">Last 3 Months</option>
              <option value="6months">Last 6 Months</option>
              <option value="1year">Last Year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Analysis Content */}
      {analysisType === 'pricing' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Price History Chart */}
          <div className="bg-white rounded-lg shadow p-4 h-80">
            <h4 className="text-md font-medium text-gray-700 mb-2 text-center">
              Gasohol 95 Price History
            </h4>
            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={priceHistoryData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis domain={['dataMin - 0.5', 'dataMax + 0.5']} />
                <Tooltip formatter={(value) => `฿${value}`} />
                <Legend />
                <Line type="monotone" dataKey="ourPrice" name="Our Price" stroke="#A100FF" strokeWidth={2} />
                <Line type="monotone" dataKey="avgCompetitorPrice" name="Avg. Competitor Price" stroke="#00C49F" strokeWidth={2} />
                <Line type="monotone" dataKey="marketAverage" name="Market Average" stroke="#999" strokeWidth={1} strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Price Sensitivity Analysis */}
          <div className="bg-white rounded-lg shadow p-4 h-80">
            <h4 className="text-md font-medium text-gray-700 mb-2 text-center">
              Price Sensitivity Analysis
            </h4>
            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={sensitivityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="priceDiff" 
                  label={{ value: 'Price Difference from Competition (฿)', position: 'insideBottom', offset: -5 }} 
                  tickFormatter={(value) => value > 0 ? `+${value}` : value}
                />
                <YAxis 
                  label={{ value: 'Volume Change (%)', angle: -90, position: 'insideLeft' }}
                  tickFormatter={(value) => value > 0 ? `+${value}%` : `${value}%`}
                />
                <Tooltip formatter={(value, name) => [`${value > 0 ? '+' : ''}${value}%`, 'Volume Change']} />
                <Line type="monotone" dataKey="volumeChange" stroke="#8884d8" dot={{ stroke: '#8884d8', strokeWidth: 2 }} activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          {/* Current Price Comparison */}
          <div className="bg-white rounded-lg shadow p-4 col-span-2">
            <h4 className="text-md font-medium text-gray-700 mb-3">
              Current Price Comparison
            </h4>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Station
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Distance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gasohol 95
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Diesel
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price Diff (95)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Updated
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr className="bg-purple-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-purple-900">
                      {station.name} (Our Station)
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      -
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ฿33.25
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ฿30.20
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      -
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      Today, 8:00 AM
                    </td>
                  </tr>
                  
                  {filteredCompetitors.map(competitor => {
                    const priceDiff = (33.25 - competitor.priceGasohol95).toFixed(2);
                    return (
                      <tr key={competitor.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {competitor.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {competitor.distance} km
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ฿{competitor.priceGasohol95}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ฿{competitor.priceDiesel}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            priceDiff > 0 
                              ? 'bg-green-100 text-green-800' 
                              : priceDiff < 0
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {priceDiff > 0 ? '+' : ''}{priceDiff}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          Today, 6:00 AM
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Price Alert System */}
            <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h5 className="text-sm font-medium text-yellow-800 mb-2">Price Alert System</h5>
              <p className="text-sm text-gray-600 mb-2">
                The system is monitoring competitor prices in real-time. You will be alerted when significant changes occur.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div className="bg-white p-3 rounded-lg border border-gray-100">
                  <div className="text-xs font-medium text-gray-500">PRICE CHANGE THRESHOLD</div>
                  <div className="text-sm mt-1 flex items-center justify-between">
                    <span>฿0.20 per liter</span>
                    <button className="text-xs text-purple-600 hover:text-purple-800">Edit</button>
                  </div>
                </div>
                <div className="bg-white p-3 rounded-lg border border-gray-100">
                  <div className="text-xs font-medium text-gray-500">NOTIFICATION PREFERENCES</div>
                  <div className="text-sm mt-1 flex items-center justify-between">
                    <span>Email, Mobile Push</span>
                    <button className="text-xs text-purple-600 hover:text-purple-800">Edit</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {analysisType === 'landscape' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Competitive Map */}
          <div className="bg-white rounded-lg shadow p-4 h-96 col-span-2">
            <h4 className="text-md font-medium text-gray-700 mb-2 text-center">
              Competitive Landscape Map
            </h4>
            <div className="h-full">
              <MapContainer 
                center={[station.lat, station.lng]} 
                zoom={14} 
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {/* Our station */}
                <Circle
                  center={[station.lat, station.lng]}
                  radius={comparisonRadius * 1000}
                  pathOptions={{ fillColor: 'purple', fillOpacity: 0.1, color: 'purple', weight: 1 }}
                />
                <Marker position={[station.lat, station.lng]}>
                  <Popup>
                    <div className="text-sm font-medium">Our Station: {station.name}</div>
                  </Popup>
                </Marker>
                
                {/* Competitor stations */}
                {filteredCompetitors.map(competitor => (
                  <Marker 
                    key={competitor.id} 
                    position={[competitor.lat, competitor.lng]}
                    icon={new L.Icon({
                      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
                      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
                      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
                      iconSize: [25, 41],
                      iconAnchor: [12, 41],
                      popupAnchor: [1, -34],
                      shadowSize: [41, 41]
                    })}
                  >
                    <Popup>
                      <div className="text-sm">
                        <p className="font-medium">{competitor.name}</p>
                        <p>Distance: {competitor.distance} km</p>
                        <p>Gasohol 95: ฿{competitor.priceGasohol95}</p>
                        <p>Diesel: ฿{competitor.priceDiesel}</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
          
          {/* Market Share */}
          <div className="bg-white rounded-lg shadow p-4 h-80">
            <h4 className="text-md font-medium text-gray-700 mb-2 text-center">
              Estimated Traffic Share
            </h4>
            <ResponsiveContainer width="100%" height="90%">
              <PieChart>
                <Pie
                  data={[
                    { name: 'Our Station', value: 25 },
                    ...filteredCompetitors.map(c => ({ name: c.name, value: c.trafficShare }))
                  ]}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  label={(entry) => `${entry.name}: ${entry.value}%`}
                >
                  {filteredCompetitors.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                  <Cell key="our-station" fill="#A100FF" />
                </Pie>
                <Tooltip formatter={(value) => `${value}%`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          {/* Customer Overlap */}
          <div className="bg-white rounded-lg shadow p-4 h-80">
            <h4 className="text-md font-medium text-gray-700 mb-2 text-center">
              Customer Base Overlap
            </h4>
            <ResponsiveContainer width="100%" height="90%">
              <PieChart>
                <Pie
                  data={overlapData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  label={(entry) => `${entry.name}: ${entry.value}%`}
                >
                  {overlapData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value}%`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          {/* Competitive Strength Radar */}
          <div className="bg-white rounded-lg shadow p-4 col-span-2 h-80">
            <h4 className="text-md font-medium text-gray-700 mb-2 text-center">
              Competitive Strength Analysis
            </h4>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart outerRadius={90} width={730} height={250} data={strengthsData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="subject" />
                <PolarRadiusAxis angle={30} domain={[0, 10]} />
                <Radar name="Our Station" dataKey="ours" stroke="#A100FF" fill="#A100FF" fillOpacity={0.6} />
                <Radar name="Competitor A" dataKey="competitorA" stroke="#00C49F" fill="#00C49F" fillOpacity={0.6} />
                <Radar name="Competitor B" dataKey="competitorB" stroke="#FFBB28" fill="#FFBB28" fillOpacity={0.6} />
                <Legend />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      
      {analysisType === 'features' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Feature Comparison Matrix */}
          <div className="bg-white rounded-lg shadow p-4 col-span-2">
            <h4 className="text-md font-medium text-gray-700 mb-3">
              Feature Comparison Matrix
            </h4>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Feature
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-purple-500 uppercase tracking-wider">
                      Our Station
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Competitor A
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Competitor B
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Competitor C
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {featureComparisonData.map((feature, index) => (
                    <tr key={index} className={feature.ours && (!feature.competitorA && !feature.competitorB && !feature.competitorC) ? 'bg-purple-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {feature.feature}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {feature.ours ? (
                          <span className="h-5 w-5 bg-green-100 text-green-800 rounded-full inline-flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </span>
                        ) : (
                          <span className="h-5 w-5 bg-red-100 text-red-800 rounded-full inline-flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {feature.competitorA ? (
                          <span className="h-5 w-5 bg-green-100 text-green-800 rounded-full inline-flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </span>
                        ) : (
                          <span className="h-5 w-5 bg-red-100 text-red-800 rounded-full inline-flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {feature.competitorB ? (
                          <span className="h-5 w-5 bg-green-100 text-green-800 rounded-full inline-flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </span>
                        ) : (
                          <span className="h-5 w-5 bg-red-100 text-red-800 rounded-full inline-flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {feature.competitorC ? (
                          <span className="h-5 w-5 bg-green-100 text-green-800 rounded-full inline-flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </span>
                        ) : (
                          <span className="h-5 w-5 bg-red-100 text-red-800 rounded-full inline-flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {analysisType === 'insights' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* AI Insights */}
          <div className="bg-white rounded-lg shadow p-4 col-span-2">
            <h4 className="text-md font-medium text-gray-700 mb-3">
              AI Insights
            </h4>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Insight
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {insights.map((insight, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {insight}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitorAnalysis;
